"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Utensils } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";

import { useRecipeSearch } from "@/app/hooks/useRecipeSearch";
import { useUserTagFilters } from "@/app/hooks/useUserTagFilters";
import { DiscoverSearchInput } from "@/components/recipes/DiscoverSearchInput";
import { InfiniteRecipeGrid } from "@/components/recipes/InfiniteRecipeGrid";
import { NewRecipeButton } from "@/components/recipes/NewRecipeButton";
import { RecipeScopeDropdown } from "@/components/recipes/RecipeScopeDropdown";
import { RecipeTagBar } from "@/components/recipes/RecipeTagBar";
import { TagChipsSkeleton } from "@/components/recipes/TagChipsSkeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { RecipeGridSkeleton } from "@/components/shared/RecipeGridSkeleton";
import { crossfadeVariants } from "@/lib/animations";
import type { OwnRecipeScope } from "@/lib/recipes/server";
import { parseTags } from "@/lib/utils";

const VALID_SCOPES: ReadonlySet<OwnRecipeScope> = new Set(["all", "favorites", "drafts"]);

export default function RecipesPage() {
  const { data: session, status: sessionStatus } = useSession();
  const userId = session?.user?.id;
  const searchParams = useSearchParams();

  const q = searchParams.get("q")?.trim() || "";
  const selectedTags = parseTags(searchParams.get("tags") ?? undefined);
  const scope = (
    VALID_SCOPES.has(searchParams.get("scope") as OwnRecipeScope)
      ? (searchParams.get("scope") as OwnRecipeScope)
      : "all"
  ) as OwnRecipeScope;

  const { data: result, isLoading: isLoadingRecipes } = useRecipeSearch({
    q: q || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
    scope,
    enabled: !!userId,
  });

  const { data: tagData, isLoading: isLoadingTags } = useUserTagFilters(!!userId);
  const userTags = tagData?.tags ?? [];

  const showGridSkeleton = sessionStatus === "loading" || (!!userId && isLoadingRecipes);

  return (
    <>
      <PageHeader
        leftSlot={<RecipeScopeDropdown selected={scope} query={q} tags={selectedTags} />}
        rightSlot={<NewRecipeButton />}
      />

      <main className="min-h-[calc(100dvh-var(--navbar-height))] overflow-x-clip">
        <div className="max-w-lg mx-auto px-3 pt-4 space-y-3 sm:pt-6">
          <DiscoverSearchInput initialQuery={q} logSearches searchSource="recipes" />
          {userId && (
            <AnimatePresence mode="wait">
              {isLoadingTags ? (
                <TagChipsSkeleton key="tags-skeleton" />
              ) : (
                <motion.div
                  key="tags"
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={crossfadeVariants}
                >
                  <RecipeTagBar initialTags={userTags} selected={selectedTags} query={q} />
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>

        <div className="px-3 py-4">
          <AnimatePresence mode="wait">
            {showGridSkeleton ? (
              <RecipeGridSkeleton key="grid-skeleton" />
            ) : !result || result.items.length === 0 ? (
              <motion.div
                key="grid-empty"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={crossfadeVariants}
              >
                <RecipesEmptyState query={q} />
              </motion.div>
            ) : (
              <motion.div
                key="grid-content"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={crossfadeVariants}
              >
                <InfiniteRecipeGrid
                  initialItems={result.items}
                  initialHasMore={result.hasMore}
                  fetchUrl={buildFetchUrl(q, selectedTags, scope)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </>
  );
}

function buildFetchUrl(q: string, tags: string[], scope: OwnRecipeScope): string {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (tags.length > 0) params.set("tags", tags.join(","));
  if (scope !== "all") params.set("scope", scope);
  return `/api/recipes/search?${params}`;
}

function RecipesEmptyState({ query }: { query: string }) {
  const t = useTranslations("recipes");
  return (
    <EmptyState
      icon={
        <div className="w-20 h-20 flex items-center justify-center">
          <Utensils className="size-full text-text-secondary" strokeWidth={1} />
        </div>
      }
      title={query ? t("noResultsTitle", { query }) : t("emptyTitle")}
      description={query ? t("noResultsHint") : t("emptyHint")}
    />
  );
}
